/**
 * The job's top box: "4 passed · 1 failed · 0 couldn't do" (Corbin, 10/28,
 * threads/job-page-fresh). The WORDS come from the job type (server:
 * services/jobOutcomes.ts, `GET /api/jobs/:id/outcomes`); this file decides
 * which outcome each device lands in, the same way on the office page and on
 * the tech's iPad.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type OutcomeKey = "passed" | "failed" | "repaired" | "replaced" | "done" | "open" | "couldnt";
export type OutcomeColor = "green" | "red" | "amber" | "grey";

export interface OutcomeDef {
    key: OutcomeKey;
    label: string;
    color: OutcomeColor;
}

export interface OutcomeSet {
    countsBy: "device" | "unit" | "task";
    before: string;
    outcomes: OutcomeDef[];
    source: "job_type" | "built_in";
}

/** The words the page falls back to while the server's are loading or missing. */
export const FALLBACK_OUTCOMES: OutcomeSet = {
    countsBy: "device",
    before: "to do",
    outcomes: [
        { key: "done", label: "done", color: "green" },
        { key: "open", label: "still to do", color: "amber" },
        { key: "couldnt", label: "couldn't do", color: "grey" },
    ],
    source: "built_in",
};

/**
 * Where one device stands after the visit, before any words are applied.
 *   passed / failed  a finished test
 *   repaired / replaced  the work is done (and the test after it passed)
 *   done        finished, no test involved
 *   open        work still to do (a repair pending, or a test after it failed)
 *   unfinished  a test that was started and not finished
 *   couldnt     the tech wrote why it couldn't be done
 *   todo        nothing recorded yet
 */
export type ItemState =
    "passed" | "failed" | "repaired" | "replaced" | "done" | "open" | "unfinished" | "couldnt" | "todo";

/** Which outcome a state lands in when the job type has no outcome of that exact name. */
const FALLBACK: Record<ItemState, OutcomeKey[]> = {
    passed: ["passed", "done"],
    failed: ["failed", "open"],
    repaired: ["repaired", "done", "passed"],
    replaced: ["replaced", "repaired", "done", "passed"],
    done: ["done", "passed"],
    open: ["open"],
    unfinished: ["open"],
    couldnt: ["couldnt"],
    todo: ["open"],
};

export interface OutcomeCount extends OutcomeDef {
    n: number;
}

export interface OutcomeTally {
    /** Before the visit: one number ("23 to test"). */
    before: boolean;
    total: number;
    counts: OutcomeCount[];
    /** Items no outcome took (a test not finished on a test job, a device with no result yet). */
    notCounted: number;
    /** Tests started and not finished: the red "1 test not finished" line. */
    unfinished: number;
}

/**
 * Count each item into the job type's outcomes. Every outcome shows, zeros
 * included, in the job type's order. Before the visit nothing is counted.
 */
export function tallyOutcomes(set: OutcomeSet, states: ItemState[], preVisit: boolean): OutcomeTally {
    const counts: OutcomeCount[] = set.outcomes.map((o) => ({ ...o, n: 0 }));
    let notCounted = 0;
    let unfinished = 0;
    if (!preVisit) {
        for (const s of states) {
            if (s === "unfinished") unfinished += 1;
            const into = FALLBACK[s].map((k) => counts.find((c) => c.key === k)).find(Boolean);
            if (into) into.n += 1;
            else notCounted += 1;
        }
    }
    return { before: preVisit, total: states.length, counts, notCounted, unfinished };
}

/** The top box's words for this job. */
export function useJobOutcomes(jobId: string | null | undefined) {
    return useQuery({
        queryKey: ["job", jobId, "outcomes"],
        queryFn: async () => (await api.get<{ outcomes: OutcomeSet }>(`/api/jobs/${jobId}/outcomes`)).outcomes,
        enabled: !!jobId,
        staleTime: 5 * 60_000,
    });
}

/** Text colour for an outcome's number (a 0 is always grey, never a false alarm). */
export function outcomeInk(color: OutcomeColor, n: number): string {
    if (n === 0) return "text-muted-foreground";
    return {
        green: "text-emerald-700 dark:text-emerald-400",
        red: "text-red-700 dark:text-red-400",
        amber: "text-amber-700 dark:text-amber-400",
        grey: "text-muted-foreground",
    }[color];
}

/** Fill for an outcome's slice of the bar. */
export function outcomeFill(color: OutcomeColor): string {
    return { green: "bg-emerald-600", red: "bg-red-600", amber: "bg-amber-500", grey: "bg-stone-400" }[color];
}
